import React from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import { useLocation, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import WarningIcon from "@mui/icons-material/Warning";
import format from "date-fns/format";
import differenceInDays from "date-fns/differenceInDays";
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
  GridActionsColDef,
  getGridStringOperators,
  getGridDateOperators,
  GridFilterOperator,
  GridFilterInputSingleSelect,
} from "@mui/x-data-grid-pro";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import {
  DataTableContainer,
  ACTIONS_COLUMN_TEXT,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
  DataGridActionsLinkButton,
} from "@app.automotus.io/components/DataTableContainer";
import {
  GET_PERMITS,
  GetPermitsData,
  GetPermitsVars,
  GetPermitsWhereFilter,
  PermitsOrderBy,
  transformPermitRaw,
} from "common/graphql";
import {
  parseSortModel,
  serializeSortModel,
  parseFilterModelFromSearchParams,
  filterModelToSearchParams,
} from "common/graphql/queryString";
import type { PermitSummary, PermitStatus } from "common/types/permits";
import PermitStatusBadge from "@curbsuite/components/permits/PermitStatusBadge";
import { PermitsTableToolbar } from "./PermitsTableToolbar";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";

const PERMITS_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  displayId: true,
  payerAccountName: true,
  permitTypeName: true,
  vehicleCount: true,
  validFrom: true,
  validTo: true,
  status: true,
  actions: true,
};

const PAGE_SIZE = 100;
const DEFAULT_SORT = "validTo+asc";
const BASE_FILTER = { status: { _neq: "pending" } } as const;

const isExpiringSoon = (validTo: string | null): boolean => {
  if (!validTo) {
    return false;
  }
  const daysUntilExpiry = differenceInDays(new Date(validTo), new Date());
  return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
};

const formatDateValue = (dateString: string | null): string => {
  if (!dateString) {
    return "—";
  }
  return format(new Date(dateString), "MMM d, yyyy");
};

export interface PermitsDataGridProps {
  permitTypeOptions?: { value: string; label: string }[];
}

export const PermitsDataGrid: React.FC<PermitsDataGridProps> = ({ permitTypeOptions = [] }) => {
  const client = useApolloClient();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: DEFAULT_SORT,
  });

  // Carried on each row's detail link so its own back button can restore this exact list view
  // even when the link is opened in a new tab, where react-router's `state` (passed separately
  // below) never arrives: a fresh tab has no access to the originating tab's in-memory history
  // state, only to whatever is in the URL itself.
  const returnTo = `${location.pathname}?${searchParams.toString()}`;

  // Page state
  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;
    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Sort state
  const sort = searchParams.get("sort") || DEFAULT_SORT;
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  // Convert sort model to GraphQL order by
  const orderBy: PermitsOrderBy[] = React.useMemo(() => {
    if (sortModel.length === 0) {
      return [{ valid_to: "asc" }];
    }
    const { field: sortField, sort: sortDirection } = sortModel[0];
    const direction = sortDirection || "asc";

    switch (sortField) {
      case "displayId":
        return [{ display_id: direction }];
      case "status":
        return [{ status: direction }];
      case "validFrom":
        return [{ valid_from: direction }];
      case "validTo":
        return [{ valid_to: direction }];
      case "createdAt":
        return [{ created_at: direction }];
      case "payerAccountName":
        return [{ payer_account: { holder_name: direction } }];
      case "permitTypeName":
        return [{ permit_type: { name: direction } }];
      default:
        return [{ valid_to: direction }];
    }
  }, [sortModel]);

  // Filter state
  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      handleApplyFilters(newFilterModel);
    }
    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  // Build GraphQL filter from filter model
  const filter = React.useMemo((): GetPermitsWhereFilter => {
    if (!appliedFilters || appliedFilters.items.length === 0) {
      return BASE_FILTER;
    }

    const columnFilters = appliedFilters.items
      .map((item) => {
        if (item.columnField === "displayId") {
          return {
            display_id: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
          };
        }
        if (item.columnField === "payerAccountName") {
          return {
            payer_account: {
              _or: [
                {
                  organization_name:
                    item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
                },
                { holder_name: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` } },
              ],
            },
          };
        }
        if (item.columnField === "permitTypeName" && item.operatorValue === "equals") {
          return {
            permit_type: { name: { _eq: item.value } },
          };
        }
        if (item.columnField === "status" && item.operatorValue === "equals") {
          return {
            status: { _eq: item.value as PermitStatus },
          };
        }
        if (item.columnField === "validFrom") {
          const filterDate = new Date(`${item.value}T00:00:00`);
          const dateStart = startOfDay(filterDate);
          const dateEnd = endOfDay(filterDate);

          switch (item.operatorValue) {
            case "is":
              return { valid_from: { _gte: dateStart.toISOString(), _lte: dateEnd.toISOString() } };
            case "before":
              return { valid_from: { _lt: dateStart.toISOString() } };
            case "after":
              return { valid_from: { _gt: dateEnd.toISOString() } };
          }
        }
        if (item.columnField === "validTo") {
          const filterDate = new Date(`${item.value}T00:00:00`);
          const dateStart = startOfDay(filterDate);
          const dateEnd = endOfDay(filterDate);

          switch (item.operatorValue) {
            case "is":
              return { valid_to: { _gte: dateStart.toISOString(), _lte: dateEnd.toISOString() } };
            case "before":
              return { valid_to: { _lt: dateStart.toISOString() } };
            case "after":
              return { valid_to: { _gt: dateEnd.toISOString() } };
          }
        }
        return undefined;
      })
      .filter(Boolean) as GetPermitsWhereFilter[];

    if (columnFilters.length === 0) {
      return { ...BASE_FILTER };
    }

    if (appliedFilters.linkOperator === "and") {
      return {
        _and: [BASE_FILTER, ...columnFilters],
      };
    } else {
      return {
        _and: [BASE_FILTER, { _or: columnFilters }],
      };
    }
  }, [appliedFilters]);

  // Query permits
  const { data, loading, error } = useQuery<GetPermitsData, GetPermitsVars>(GET_PERMITS, {
    variables: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      orderBy,
      filter,
    },
    fetchPolicy: "network-only",
  });

  // Transform data for DataGrid
  const rows: PermitSummary[] = React.useMemo(() => {
    return data?.permits.map(transformPermitRaw) || [];
  }, [data?.permits]);

  const rowCount = data?.permitsAggregate.aggregate.count || 0;

  // Export functions
  const fetchTotalCount = async () => {
    const result = await client.query<GetPermitsData, GetPermitsVars>({
      query: GET_PERMITS,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
      },
    });
    return result.data?.permitsAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetPermitsData, GetPermitsVars>({
      query: GET_PERMITS,
      variables: {
        offset,
        limit,
        orderBy,
        filter,
      },
    });
    return result.data?.permits.map(transformPermitRaw) || [];
  };

  // Column definitions
  const columns: GridColDef<PermitSummary>[] = React.useMemo(() => {
    const displayIdColumn: GridColDef<PermitSummary> = {
      field: "displayId",
      headerName: "Permit ID",
      width: 140,
      sortable: true,
      filterOperators: getGridStringOperators().filter((op) =>
        ["equals", "contains"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, string, string>[],
      renderCell: (params) => <Typography sx={{ fontFamily: "monospace", fontWeight: 500 }}>{params.value}</Typography>,
    };

    const payerAccountNameColumn: GridColDef<PermitSummary> = {
      field: "payerAccountName",
      headerName: "Operator",
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (params) => params.row.payerAccount?.organizationName || params.row.payerAccount?.holderName || "—",
      filterOperators: getGridStringOperators().filter((op) =>
        ["equals", "contains"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, string, string>[],
    };

    const permitTypeNameColumn: GridColDef<PermitSummary> = {
      field: "permitTypeName",
      headerName: "Permit Type",
      width: 180,
      sortable: false,
      valueGetter: (params) => params.row.permitType?.name || "—",
      type: "singleSelect",
      valueOptions: permitTypeOptions,
      filterOperators: getGridStringOperators()
        .filter((op) => ["equals"].includes(op.value))
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<PermitSummary, string, string>[],
    };

    const vehicleCountColumn: GridColDef<PermitSummary> = {
      field: "vehicleCount",
      headerName: "Vehicles",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    };

    const validFromColumn: GridColDef<PermitSummary> = {
      field: "validFrom",
      headerName: "Valid From",
      width: 130,
      type: "date",
      sortable: true,
      valueGetter: (params) => (params.row.validFrom ? new Date(params.row.validFrom) : null),
      valueFormatter: (params) => formatDateValue(params.value?.toISOString() || null),
      filterOperators: getGridDateOperators().filter((op) =>
        ["is", "before", "after"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, Date, string>[],
    };

    const validToColumn: GridColDef<PermitSummary> = {
      field: "validTo",
      headerName: "Valid Until",
      width: 170,
      type: "date",
      sortable: true,
      valueGetter: (params) => (params.row.validTo ? new Date(params.row.validTo) : null),
      filterOperators: getGridDateOperators().filter((op) =>
        ["is", "before", "after"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, Date, string>[],
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>{formatDateValue(params.row.validTo)}</Typography>
          {isExpiringSoon(params.row.validTo) && (
            <Chip icon={<WarningIcon />} label="Expiring soon" size="small" color="warning" />
          )}
        </Box>
      ),
    };

    const statusColumn: GridColDef<PermitSummary> = {
      field: "status",
      headerName: "Status",
      width: 130,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "active", label: "Active" },
        { value: "expired", label: "Expired" },
        { value: "denied", label: "Denied" },
        { value: "suspended", label: "Suspended" },
        { value: "pending_suspension", label: "Pending Suspension" },
      ],
      filterOperators: getGridStringOperators()
        .filter((op) => ["equals"].includes(op.value))
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<PermitSummary, string, string>[],
      renderCell: (params) => (
        <PermitStatusBadge type={params.value === "denied" ? "application" : "permit"} status={params.value} />
      ),
    };

    const actionsColumn: GridActionsColDef<PermitSummary> = {
      ...ACTIONS_COLUMN_TEXT,
      getActions: (params) => [
        <DataGridActionsLinkButton
          to={`/permits/${params.row.id}?returnTo=${encodeURIComponent(returnTo)}`}
          state={{ from: location.pathname }}
        >
          View
        </DataGridActionsLinkButton>,
      ],
    };

    return [
      displayIdColumn,
      payerAccountNameColumn,
      permitTypeNameColumn,
      vehicleCountColumn,
      validFromColumn,
      validToColumn,
      statusColumn,
      actionsColumn,
    ];
  }, [location.pathname, returnTo, permitTypeOptions]);

  return (
    <DataTableContainer
      tableName="permits-table"
      defaultVisibilityModel={PERMITS_DEFAULT_VISIBILITY}
      sx={{ height: "100%" }}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <DataGridPro
          apiRef={apiRef}
          localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
          components={{
            Toolbar: PermitsTableToolbar,
            FilterPanel: ManualApplyGridFilterPanel,
            ColumnsPanel: FilteredColumnsPanel,
          }}
          componentsProps={{
            filterPanel: {
              onApplyFilters: handleApplyFilters,
            },
            toolbar: {
              fetchTotalCount,
              fetchDataBatch,
              exportFileName: `permits_all_${Date.now()}`,
              columns,
              defaultVisibilityModel: PERMITS_DEFAULT_VISIBILITY,
              isIconOnly,
              resetLayout,
            },
          }}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          {...STANDARD_GRID_PROPS}
          error={error}
          pagination
          page={page - 1}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
          paginationMode="server"
          rowsPerPageOptions={[PAGE_SIZE]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          initialState={initialState}
          sx={{
            flexGrow: 1,
            ...STANDARD_COLUMN_HEADER_TITLE_SX,
          }}
        />
      )}
    </DataTableContainer>
  );
};

export default PermitsDataGrid;
